import { FC, useCallback, useMemo } from 'react'
import { Box, Button, Stack } from '@mui/material'
import RightPointingArrowIcon from '../../assets/images/right-pointing-arrow.svg?react'
import LeftPointingArrowIcon from '../../assets/images/left-pointing-arrow.svg?react'
import { PrimitiveAtom, useAtom } from 'jotai'

export type TokenPaginatorProps = {
  pageTokensAtom: PrimitiveAtom<string[]>
  currentToken?: string
  nextToken?: string
  onClick?: (nextToken?: string) => void
}

const FIRST_PAGE_TOKEN = 'FIRST_PAGE_TOKEN'

export const TokenPaginator: FC<TokenPaginatorProps> = ({ pageTokensAtom, currentToken, nextToken, onClick }) => {
  // paged tokens state
  const [previousPageTokens, setPreviousPageTokens] = useAtom(pageTokensAtom)

  const pushPageToken = useCallback(
    (nextToken: string) => {
      setPreviousPageTokens(prev => {
        return [...prev, nextToken]
      })
    },
    [setPreviousPageTokens]
  )
  const popPageToken = useCallback(() => {
    setPreviousPageTokens(prev => {
      return prev.slice(0, prev.length - 1)
    })
  }, [setPreviousPageTokens])

  const previousPageToken = useMemo(() => previousPageTokens[previousPageTokens.length - 1], [previousPageTokens])

  const goToPreviousPage = useCallback(() => {
    popPageToken()
    if (previousPageToken === FIRST_PAGE_TOKEN) {
      onClick?.()
    } else {
      onClick?.(previousPageToken)
    }
  }, [onClick, previousPageToken, popPageToken])

  const goToNextPage = useCallback(() => {
    pushPageToken(currentToken ?? FIRST_PAGE_TOKEN)
    onClick?.(nextToken)
  }, [currentToken, onClick, nextToken, pushPageToken])

  if (previousPageToken || nextToken) {
    return (
      <Stack direction={'row'} sx={{ justifyContent: 'end', margin: 'var(--spacing-s)' }} spacing={'var(--spacing-s)'}>
        {previousPageToken && (
          <Box>
            <Button variant="text" onClick={goToPreviousPage} sx={{ padding: 0, minWidth: 0 }} data-testid="previous-page">
              <LeftPointingArrowIcon />
            </Button>
          </Box>
        )}
        {nextToken && (
          <Box>
            <Button variant="text" onClick={goToNextPage} sx={{ padding: 0, minWidth: 0 }} data-testid="next-page">
              <RightPointingArrowIcon />
            </Button>
          </Box>
        )}
      </Stack>
    )
  }
  return null
}
